<template>
    <div
        ref="videoContainer"
        class="d-flex justify-content-center align-items-center overflow-hidden"
        @click.self="$emit('close')"
    >
        <section
            v-if="state !== 'error'"
            tabindex="0"
            class="position-relative"
            :class="fullScreen && 'position-fixed left top vw-100 vh-100'"
            style="min-height: 135px; min-width: 240px"
            @mouseenter="showControls"
            @mousemove="showControls"
            @mouseleave="hideControls"
            @keydown.space.prevent="playToggle"
            @keydown.left="jumpBackwards"
            @keydown.right="jumpForward"
        >
            <video
                :key="key"
                :ref="`video-${key}`"
                :poster="posterUrl"
                :class="
                    fullScreen ? 'position-fixed left top vw-100 vh-100' : ''
                "
                :src="url"
                :preload="preload"
                class="d-block mxw-100 mx-auto text-center"
                style="max-height: calc(100vh - 50px)"
                playsinline
                @timeupdate="updateTimeline"
                @ended="finishVideo"
                @loadedmetadata="loadedmetadata"
                @playing.once="$emit('accessed')"
                @playing="handlePlaying"
                @canplaythrough.once="handleCanPlay"
                @volumechange="volumechange"
                @error="error"
            />

            <div
                class="transition-opacity transition-duration-75 transition-ease w-100 h-100 position-absolute abs-center bg--black--50"
                :class="visibleControls ? 'opacity-100' : 'opacity--0'"
                @click.left.self="playToggle"
                @dblclick.self="fullScreenToggle"
            >
                <actions-button
                    ref="actions"
                    class="position-absolute"
                    style="top: 2rem; right: 7rem"
                    :actions="actions"
                    :aria-label="playbackSpeedLabel"
                    icon="config"
                    theme="light"
                    @action="changeSpeed"
                />

                <tool-tip name="actions">
                    {{ playbackSpeedLabel }}
                </tool-tip>

                <div
                    class="position-absolute"
                    :class="
                        !fullScreenSupported && 'd-flex justify-content-end'
                    "
                    style="height: 72px; width: 80px; top: 2rem"
                    :style="
                        fullScreenSupported ? 'right: 1rem;' : 'right: 2rem;'
                    "
                    @mouseleave="showVolumeBar = false"
                >
                    <button-link
                        ref="mute"
                        :aria-label="
                            ucfirst(
                                isMuted
                                    ? $t('common.activateSound')
                                    : $t('common.silence')
                            )
                        "
                        :icon="isMuted ? 'volume-mute' : 'volume'"
                        theme="light"
                        @click="mute"
                        @mouseenter="showVolumeBar = true"
                    />

                    <tool-tip name="mute">
                        {{
                            ucfirst(
                                isMuted
                                    ? $t("common.activateSound")
                                    : $t("common.silence")
                            )
                        }}
                    </tool-tip>

                    <transition name="fade">
                        <volume-bar
                            v-show="$mq.sPlus && showVolumeBar"
                            :value="volume"
                            style="width: 56px; top: 36px"
                            class="text-light cursor--pointer position-absolute"
                            @update:value="adjustVolume"
                        />
                    </transition>
                </div>

                <template v-if="fullScreen">
                    <button-link
                        ref="exitFullScreen"
                        :aria-label="ucfirst($t('common.exitFullScreen'))"
                        icon="fullscreen-exit"
                        style="right: 2rem; top: 2rem"
                        class="position-absolute"
                        theme="light"
                        @click="fullScreenExit"
                    />

                    <tool-tip name="exitFullScreen">
                        {{ ucfirst($t("common.exitFullScreen")) }}
                    </tool-tip>
                </template>

                <template v-else>
                    <button-link
                        v-if="fullScreenSupported"
                        ref="enterFullScreen"
                        icon="focus"
                        :aria-label="ucfirst($t('common.enterFullScreen'))"
                        style="right: 2rem; top: 2rem"
                        class="position-absolute"
                        theme="light"
                        @click="fullScreenEnter"
                    />

                    <tool-tip
                        v-if="fullScreenSupported"
                        name="enterFullScreen"
                    >
                        {{ ucfirst($t("common.enterFullScreen")) }}
                    </tool-tip>
                </template>

                <div class="position-absolute abs-center">
                    <v-icon
                        v-if="state === 'created'"
                        class="text-light"
                        name="spinner"
                        spin
                        :w="32"
                    />

                    <play-button
                        v-else
                        ref="pause-play"
                        :aria-label="
                            ucfirst(
                                state === 'playing'
                                    ? $t('common.pause')
                                    : $t('common.play')
                            )
                        "
                        :is-playing="state === 'playing'"
                        :size="64"
                        @click="playToggle"
                    />
                </div>

                <div
                    class="position-absolute bottom px-32 pb-16 w-100 d-flex align-items-center"
                >
                    <time-bar
                        class="text-light"
                        :current-time="currentTime"
                        :duration="totalDuration"
                        @input="newTime => updateProgress(newTime)"
                    />
                </div>
            </div>
        </section>

        <div
            v-else
            class="p-32 text-center text-white w-100"
        >
            <p>{{ ucfirst(errorMessage) }}</p>

            <base-button
                icon="sync"
                :icon-size="20"
                @click="restart"
            >
                {{ $t("common.tryAgain") }}
            </base-button>
        </div>
    </div>
</template>

<script>
import {
    clamp,
    exitFullscreen,
    focusFirstFocusable,
    isFullScreenSupported,
    requestFullscreen,
    ucfirst,
} from "~/lib/utils"
import { useListenerManager } from "front-toolkit/plugins/ListenersPlugin"

import { leaveBreadcrumb, notify } from "~/lib/bugsnag"

const PROGRESS_INTERVAL = 10000 // 10 seconds in ms
const JUMP_LENGTH = 5 // 5 seconds

import ActionsButton from "~/components/buttons/ActionsButton"
import BaseButton from "~/components/buttons/BaseButton"
import ButtonLink from "~/components/buttons/ButtonLink"
import PlayButton from "~/components/buttons/PlayButton"
import TimeBar from "~/components/TimeBar"
import ToolTip from "~/components/ToolTip"
import VolumeBar from "~/components/players/Video/VolumeBar"

/*
 * State diagram:
 *
 *                 ┌───────┐
 *             ┌───┤created◄──┐
 *             │   └───────┘  │ restart()
 *  @loadedmetadata           │
 *             │              │
 *          ┌──▼───┐       ┌──┴──┐
 *          │paused│       │error│
 *          └─▲──┬─┘       └──▲──┘
 *   paused() │  │ play()     │
 *            │  │            │ @error
 *          ┌─┴──▼──┐
 *          │playing│
 *          └───────┘
 *
 */
export default {
    props: {
        url: {
            type: String,
            required: true,
        },

        autoplay: {
            type: Boolean,
            default: true,
        },

        canAdvance: {
            type: Boolean,
            default: true,
        },

        trackProgress: {
            type: Boolean,
            default: false,
        },

        posterUrl: {
            type: String,
            default: "",
        },

        initialTime: {
            type: Number,
            default: 0,
        },

        preload: {
            type: String,
            default: "auto",
        },

        focusOnMount: {
            type: Boolean,
            default: false,
        },
    },

    emits: ["close", "accessed", "progress"],

    setup() {
        const { listen } = useListenerManager()
        return { listen }
    },

    data() {
        return {
            key: 0,
            currentTime: 0,
            totalDuration: 0,

            // This "complicated" management is due to Safari iOS not allowing changing
            // the volume via JS. This way, at least we don't lie and show the true volume.
            volume: 1,
            targetVolume: 1,
            previousTargetVolume: 1,

            isMuted: false,
            playbackSpeed: 1,
            fullScreen: false,
            errorMessage: "",
            state: "created", // "created" | "paused" | "playing" | "error"

            showControlsTimer: null,
            showControlsCountDown: 2,
            showVolumeBar: false,
        }
    },

    computed: {
        actions() {
            return [0.5, 1, 1.5, 2, 3].map(speed => ({
                text: `${speed}x`,
                event: speed,
                icon:
                    speed === this.playbackSpeed
                        ? "radiobutton-check"
                        : "radiobutton-empty",
            }))
        },

        playbackSpeedLabel() {
            return `${this.ucfirst(this.$t("common.playbackSpeed"))} (${this.playbackSpeed}x)`
        },

        video() {
            return this.$refs[`video-${this.key}`]
        },

        visibleControls() {
            return this.showControlsCountDown > 0
        },

        progress() {
            if (!this.totalDuration) return 0
            return (100 * this.currentTime) / this.totalDuration
        },

        fullScreenSupported() {
            return isFullScreenSupported(document.body)
        },
    },

    watch: {
        trackProgress(track) {
            if (!track) {
                this.stopProgressTimer()
            }
        },
    },

    mounted() {
        if (this.focusOnMount) {
            focusFirstFocusable(this.$refs.videoContainer)
        }
        this.listen("fullscreenchange", () => {
            this.fullScreen = !!document.fullscreenElement
        })
        this.listen("mozfullscreenchange", () => {
            this.fullScreen = !!document.mozFullScreen
        })
        this.listen("webkitfullscreenchange", () => {
            this.fullScreen = !!document.webkitIsFullScreen
        })
    },

    beforeUnmount() {
        if (this.trackProgress) {
            this.sendProgress({ isClosing: true })
        }
        this.stopProgressTimer()
        if (!this.video) return
        // https://stackoverflow.com/questions/3258587/how-to-properly-unload-destroy-a-video-element
        this.pause()
        this.video.removeAttribute("src")
        this.video.load()
    },

    methods: {
        loadedmetadata() {
            // This used to be done on canplaythrough, but that event is not fired on Safari
            // if the video doesn't "autoplay".
            // Both the `autoplay` attribute and calls to `video.play()` are considered "autoplay".
            // There are pretty strict requirements for allowing autoplay (https://developer.mozilla.org/en-US/docs/Web/Media/Autoplay_guide#autoplay_availability)
            // so we can't rely on those events being triggered automatically.
            this.state = "paused"

            this.video.playbackRate = this.playbackSpeed
        },

        handleCanPlay() {
            if (!this.video) return

            if (this.initialTime && this.key === 0) {
                leaveBreadcrumb(
                    `Let's advance the video until ${this.initialTime}s`
                )
                this.video.currentTime = this.initialTime
            }
            if (this.currentTime && this.key > 0) {
                leaveBreadcrumb(
                    `The video is recovering from an error and needs to advance until ${this.currentTime}s`
                )
                this.video.currentTime = this.currentTime
                this.play()
            }

            if (this.autoplay) {
                this.play()
            }
        },

        error() {
            const { error } = this.video

            if (!error) return
            if (error.code === MediaError.MEDIA_ERR_ABORTED) return

            leaveBreadcrumb(
                `There has been an error at time ${this.currentTime}s`
            )
            notify(error)

            this.errorMessage =
                error.message ?? this.$t(`errors.video.${error.code}`)
            this.state = "error"
        },

        playToggle() {
            leaveBreadcrumb(
                `The user pressed ${this.video.paused ? "play" : "pause"} button while the progress is ${this.progress}`
            )
            this.video.paused ? this.play() : this.pause()
        },

        startProgressTimerIfNeeded() {
            if (!this._timer && this.trackProgress) {
                this._timer = window.setInterval(
                    this.sendProgress,
                    PROGRESS_INTERVAL
                )
            }
        },

        stopProgressTimer() {
            window.clearInterval(this._timer)
            this._timer = null
        },

        play() {
            if (!this.video) {
                leaveBreadcrumb("There is no video, I can't play no video")
                return
            }

            this.startProgressTimerIfNeeded()

            const promise = this.video.play()
            if (promise) {
                promise.catch(() => {
                    // Avoid "Unhandled Promise Rejection" error messages
                })
            }
        },

        handlePlaying() {
            this.state = "playing"
            this.showControlsCountDown = 0
        },

        pause() {
            if (!this.video) return

            this.state = "paused"
            this.showControlsCountDown = 2
            this.stopProgressTimer()
            this.video.pause()
        },

        updateTimeline() {
            if (!this.video) return

            const totalDuration = this.video.duration
            const currentTime = this.video.currentTime

            this.currentTime = currentTime || this.currentTime
            this.totalDuration = totalDuration || this.totalDuration
        },

        updateProgress(newTime) {
            if (!this.canAdvance) return

            this.currentTime = newTime
            this.video.currentTime = newTime
        },

        mute() {
            if (this.targetVolume == 0) {
                this.targetVolume = this.previousTargetVolume
            } else {
                this.previousTargetVolume = this.targetVolume
                this.targetVolume = 0
            }
            this.applyVolume()
        },

        adjustVolume(newVolume) {
            this.previousTargetVolume = this.targetVolume
            this.targetVolume = newVolume
            this.applyVolume()
        },

        applyVolume() {
            if (!this.video) return
            this.video.volume = this.targetVolume
        },

        changeSpeed(newSpeed) {
            this.playbackSpeed = newSpeed

            if (!this.video) return

            this.video.playbackRate = newSpeed
        },

        showControls() {
            this.showControlsCountDown = 2
            window.clearInterval(this.showControlsTimer)
            this.showControlsTimer = window.setInterval(() => {
                if (this.state === "playing") {
                    this.showControlsCountDown--
                }
            }, 1000)
        },

        hideControls() {
            if (this.state === "playing") {
                this.showControlsCountDown = 0
            }
        },

        fullScreenToggle() {
            if (!this.fullScreenSupported) return

            if (!this.fullScreen) {
                this.fullScreenEnter()
            } else {
                this.fullScreenExit()
            }
        },

        fullScreenEnter() {
            requestFullscreen(this.$refs.videoContainer)
            const focusTarget = this.$refs["videoContainer"]
            focusFirstFocusable(focusTarget)
        },

        fullScreenExit() {
            exitFullscreen()
        },

        jumpBackwards() {
            if (!this.canAdvance) return

            let newTime = clamp(
                this.video.currentTime - JUMP_LENGTH,
                0,
                this.video.duration
            )
            this.video.currentTime = newTime
            this.currentTime = newTime ?? this.currentTime
        },

        jumpForward() {
            if (!this.canAdvance) return

            let newTime = clamp(
                this.video.currentTime + JUMP_LENGTH,
                0,
                this.video.duration
            )
            this.video.currentTime = newTime
            this.currentTime = newTime ?? this.currentTime
        },

        sendProgress({ progress, isClosing } = {}) {
            if (!this.video || !this.trackProgress) return

            this.$emit("progress", {
                progress: progress || this.progress,
                playBackTime:
                    this.state === "playing" ? PROGRESS_INTERVAL / 1000 : 0,
                currentTime: this.video.currentTime,
                isClosing,
            })
        },

        finishVideo() {
            this.pause()
            this.stopProgressTimer()
            this.trackProgress && this.sendProgress({ progress: 100 })
        },

        volumechange() {
            this.volume = this.video.volume
            this.isMuted = this.targetVolume === 0
        },

        restart() {
            leaveBreadcrumb(
                `The user tried to restart the video, the progress was ${this.progress} right before reseting the video`
            )
            this.state = "created"
            this.key++
        },

        ucfirst,
    },

    components: {
        ActionsButton,
        BaseButton,
        ButtonLink,
        PlayButton,
        TimeBar,
        ToolTip,
        VolumeBar,
    },
}
</script>
